import React, { useState, useEffect, useContext } from 'react'
import Router from 'next/router'
import { GiTrophiesShelf } from 'react-icons/gi'
import Error from 'next/error'
import { Button } from "@heroui/react"

import Layout from '../../../components/layout'
import ContentArea from '../../../components/content-area'
import Header from '../../../components/header'
import Footer from '../../../components/footer'
import Divider from '../../../components/divider'
import ChallengeBox from '../../../components/challenge-box'
import GroupHeader from '../../../components/group-header'
import JoinContainer from '../../../components/join-container'

import { challengeSorter } from '../../../src/sorters'
import { capitalizeFirstLetter } from '../../../src/formatter'

import useHasMounted from '../../../hooks/use-has-mounted'
import { UserContext } from '../../../contexts/user-context'

const Group = ({ group, archived, active, error }) => {
  const [showJoinMsg, setShowJoinMsg] = useState(false)

  const hasMounted = useHasMounted()
  const userHook = useContext(UserContext)
  const user = userHook.user

  let showJoinContainer = false
  if (group.type === 'event' && (!user.id || (user.id && !user.challengesJoined.find(c => c.id === group.challenges[0].id)))) {
    showJoinContainer = true
  }

  let isOrgAdmin = false
  if (user && user.organizationId === group.organizationId) {
    isOrgAdmin = true
  }

  let showViewOverall = false
  if (user && user.id && group.type === 'event' && user.challengesJoined.find(c => c.id === group.challenges[0].id)) {
    showViewOverall = true
  }
  
  useEffect(() => {
    // show top green banner congratulating join
    if (window.joined && window.joined[group.id]) {
      setShowJoinMsg(true)
      window.joined[group.id] = undefined
    }
  }, [])  

  if (!group) return <Error statusCode={404} />

  return (
    <Layout
      title={`${group.name || 'Group'} - (powered by Challenge Hound)`}
      description={group.description}
      keywords={group.description}
      image={group.logo}
      noRobots={!group.private}>
      <Header section='groups' />
      <main className={group.theme}>
        <ContentArea>
          {hasMounted && <>
            <GroupHeader user={user || {}} group={group} page={'challenges'} />

            {showJoinMsg && 
              <div className='border-1 border-success p-4 rounded-md' closeFn={() => { setShowJoinMsg('') }}>
                <strong>Congratulations!</strong> You have successfully joined the <strong>{group.name}</strong>! You will automatically receive email updates after each activity you record. You can also come back to Challenge Hound at any time to view your progress and compare your results.
              </div>
            }

            {showViewOverall && <div className='mt-4 mb-16 md:mb-2'>
              <div className='mb-8 flex justify-center'>
                <Button
                  onPress={() => {
                    Router.push(`/group/[id]/reports`, `/group/${group.id}/reports`)
                  }}
                  color='primary'
                  size='lg'
                  startContent={<GiTrophiesShelf style={{ fontSize: '25px' }} />} >
                    View Overall Results
                </Button>
              </div>
            </div>}

            {Boolean(active.length) && <>
              <Divider>Active {capitalizeFirstLetter(group.type)}s</Divider>
              
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {active.sort(challengeSorter).map(challenge => (
                  <ChallengeBox user={user} key={challenge.id} group={group} challenge={challenge} />
                ))}
              </div>
            </>}

            {showJoinContainer && <div style={{textAlign: 'center', marginBottom: '50px', marginTop: '50px'}}>
              <JoinContainer user={user} challenge={Object.assign(group.challenges[0], { group: group })} passThroughCode={''} />
            </div>}
            
            {Boolean(archived.length) &&
              <div className='mt-20'>
                <Divider>Archived {capitalizeFirstLetter(group.type)}s</Divider>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  {archived.map(challenge => (
                    <ChallengeBox user={user} key={challenge.id} group={group} challenge={challenge} />
                  ))}
                </div>
              </div>
            }
          </>}
            
        </ContentArea>
      </main>
      <Footer />
    </Layout>
  )
}

export async function getServerSideProps(context) {
  const groupId = context.query.id

  try {
    const getConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
    const res = await fetch(`${process.env.API}/groups/${groupId}`, getConfig)
    if (!res.ok) {
      const error = await res.text()
      console.error(error)
      return { notFound: true }
    }

    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=30, stale-while-revalidate=59'
    )

    const group = await res.json()

    const archived = group.challenges.filter(challenge => challenge.over).sort((a, b) => {
      const aStart = new Date(a.start).getTime()
      const bStart = new Date(b.start).getTime()
      if (aStart < bStart) {
        return 1
      } else if (aStart > bStart) {
        return -1
      } else {
        return 0
      }
    }).map(item => {
      delete item.completed // irrelevant
      return item
    })
    const active = group.challenges.filter(challenge => !challenge.over).map(item => {
      delete item.completed // irrelevant
      return item
    })

    return {
      props: {
        group,
        archived,
        active
      }
    }
  } catch (error) {
    console.error(error)
    return { notFound: true }
  }
}


export default Group
